import { Palette, PartyPopper, Zap } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ConfettiAction {
  particleCount?: number;
  spread?: number;
  colors?: string[];
  shapes?: ("square" | "circle")[];
  ticks?: number;
  gravity?: number;
  startVelocity?: number;
  decay?: number;
  scalar?: number;
  mode?: "confetti" | "fireworks" | "snow" | "stars" | "hearts" | "burst";
}

interface ConfettiDialogProps {
  onAdd: (confettiAction: ConfettiAction) => void;
  initialAction?: ConfettiAction;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const DEFAULT_COLORS = [
  "#ff6b6b",
  "#4ecdc4",
  "#45b7d1",
  "#96ceb4",
  "#feca57",
  "#ff9ff3",
  "#54a0ff",
  "#ff4757",
  "#2ed573",
  "#1e90ff",
  "#ffa502",
  "#ff6348",
  "#9c88ff",
  "#f368e0",
];

const PRESET_COLORS = [
  {
    name: "Rainbow",
    colors: ["#ff6b6b", "#4ecdc4", "#45b7d1", "#96ceb4", "#feca57", "#ff9ff3", "#54a0ff"],
  },
  { name: "Fire", colors: ["#ff4757", "#ff6348", "#ffa502", "#feca57", "#ff9ff3"] },
  { name: "Ocean", colors: ["#1e90ff", "#45b7d1", "#4ecdc4", "#96ceb4", "#54a0ff"] },
  { name: "Sunset", colors: ["#ff6b6b", "#ff9ff3", "#f368e0", "#9c88ff", "#54a0ff"] },
  { name: "Forest", colors: ["#2ed573", "#96ceb4", "#4ecdc4", "#45b7d1", "#54a0ff"] },
];

const MODE_PRESETS = {
  confetti: {
    name: "Confetti",
    colors: ["#ff6b6b", "#4ecdc4", "#45b7d1", "#96ceb4", "#feca57", "#ff9ff3", "#54a0ff"],
  },
  fireworks: {
    name: "Fireworks",
    colors: ["#ff6b6b", "#4ecdc4", "#45b7d1", "#96ceb4", "#feca57", "#ff9ff3", "#54a0ff"],
  },
  snow: { name: "Snow", colors: ["#ffffff", "#f0f8ff", "#e6f3ff", "#ddeeff"] },
  stars: { name: "Stars", colors: ["#ffd700", "#ffed4e", "#fff176", "#ffeb3b", "#ffc107"] },
  hearts: { name: "Hearts", colors: ["#ff69b4", "#ff1493", "#ffc0cb", "#ffb6c1", "#ffa0d0"] },
  burst: {
    name: "Burst",
    colors: ["#ff6b6b", "#4ecdc4", "#45b7d1", "#96ceb4", "#feca57", "#ff9ff3", "#54a0ff"],
  },
};

export const ConfettiDialog: React.FC<ConfettiDialogProps> = ({
  onAdd,
  initialAction,
  isOpen,
  onOpenChange,
}) => {
  const [particleCount, setParticleCount] = useState(100);
  const [spread, setSpread] = useState(70);
  const [colors, setColors] = useState<string[]>(DEFAULT_COLORS.slice(0, 7));
  const [shapes, setShapes] = useState<("square" | "circle")[]>(["square", "circle"]);
  const [ticks, setTicks] = useState(200);
  const [gravity, setGravity] = useState(0.8);
  const [startVelocity, setStartVelocity] = useState(30);
  const [decay, setDecay] = useState(0.94);
  const [scalar, setScalar] = useState(1);
  const [customColor, setCustomColor] = useState("#ff6b6b");
  const [mode, setMode] = useState<
    "confetti" | "fireworks" | "snow" | "stars" | "hearts" | "burst"
  >("confetti");

  useEffect(() => {
    if (initialAction) {
      setParticleCount(initialAction.particleCount || 100);
      setSpread(initialAction.spread || 70);
      setColors(initialAction.colors || DEFAULT_COLORS.slice(0, 7));
      setShapes(initialAction.shapes || ["square", "circle"]);
      setTicks(initialAction.ticks || 200);
      setGravity(initialAction.gravity || 0.8);
      setStartVelocity(initialAction.startVelocity || 30);
      setDecay(initialAction.decay || 0.94);
      setScalar(initialAction.scalar || 1);
      setMode(initialAction.mode || "confetti");
    }
  }, [initialAction]);

  const handlePresetColorSelect = (preset: (typeof PRESET_COLORS)[0]) => {
    setColors(preset.colors);
  };

  const handleAddCustomColor = () => {
    if (customColor && !colors.includes(customColor)) {
      setColors([...colors, customColor]);
    }
  };

  const handleRemoveColor = (colorToRemove: string) => {
    setColors(colors.filter((color) => color !== colorToRemove));
  };

  const handleShapeToggle = (shape: "square" | "circle") => {
    if (shapes.includes(shape)) {
      setShapes(shapes.filter((s) => s !== shape));
    } else {
      setShapes([...shapes, shape]);
    }
  };

  const handleModeChange = (
    newMode: "confetti" | "fireworks" | "snow" | "stars" | "hearts" | "burst",
  ) => {
    setMode(newMode);
    // Apply mode-specific colors
    const modePreset = MODE_PRESETS[newMode];
    if (modePreset) {
      setColors(modePreset.colors);
    }
  };

  const handleSubmit = () => {
    const confettiAction: ConfettiAction = {
      particleCount,
      spread,
      colors,
      shapes,
      ticks,
      gravity,
      startVelocity,
      decay,
      scalar,
      mode,
    };
    onAdd(confettiAction);
    onOpenChange(false);
  };

  const handleTestConfetti = () => {
    // Import confetti service dynamically
    import("@/utils/confettiService").then(({ confettiService }) => {
      confettiService.triggerConfetti({
        particleCount,
        spread,
        colors,
        shapes,
        ticks,
        gravity,
        startVelocity,
        decay,
        scalar,
        mode,
      });
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-800 border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center">
            <PartyPopper size={20} className="mr-2" />
            {initialAction ? "Edit Confetti Action" : "Add Confetti Action"}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Configure a confetti animation that triggers when this component is clicked.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Mode Selection */}
          <div className="space-y-4">
            <h4 className="text-white font-medium flex items-center">
              <PartyPopper size={16} className="mr-2" />
              Effect Mode
            </h4>

            <div className="space-y-2">
              <Label className="text-gray-300">Select Effect Type</Label>
              <Select value={mode} onValueChange={handleModeChange}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue placeholder="Select effect mode" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="confetti" className="text-white hover:bg-gray-700">
                    🎉 Confetti - Classic celebration
                  </SelectItem>
                  <SelectItem value="fireworks" className="text-white hover:bg-gray-700">
                    🎆 Fireworks - Multiple bursts
                  </SelectItem>
                  <SelectItem value="snow" className="text-white hover:bg-gray-700">
                    ❄️ Snow - Gentle falling particles
                  </SelectItem>
                  <SelectItem value="stars" className="text-white hover:bg-gray-700">
                    ⭐ Stars - Golden sparkles
                  </SelectItem>
                  <SelectItem value="hearts" className="text-white hover:bg-gray-700">
                    💖 Hearts - Romantic pink hearts
                  </SelectItem>
                  <SelectItem value="burst" className="text-white hover:bg-gray-700">
                    💥 Burst - Rapid explosions
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Basic Settings */}
          <div className="space-y-4">
            <h4 className="text-white font-medium flex items-center">
              <Zap size={16} className="mr-2" />
              Basic Settings
            </h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Particle Count</Label>
                <Input
                  type="number"
                  min="1"
                  max="500"
                  value={particleCount}
                  onChange={(e) => setParticleCount(parseInt(e.target.value, 10) || 100)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Spread (degrees)</Label>
                <Input
                  type="number"
                  min="0"
                  max="360"
                  value={spread}
                  onChange={(e) => setSpread(parseInt(e.target.value, 10) || 70)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Gravity</Label>
                <Input
                  type="number"
                  min="0"
                  max="2"
                  step="0.1"
                  value={gravity}
                  onChange={(e) => setGravity(parseFloat(e.target.value) || 0.8)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Start Velocity</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={startVelocity}
                  onChange={(e) => setStartVelocity(parseInt(e.target.value, 10) || 30)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Duration (ticks)</Label>
                <Input
                  type="number"
                  min="50"
                  max="500"
                  value={ticks}
                  onChange={(e) => setTicks(parseInt(e.target.value, 10) || 200)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Decay</Label>
                <Input
                  type="number"
                  min="0.8"
                  max="1"
                  step="0.01"
                  value={decay}
                  onChange={(e) => setDecay(parseFloat(e.target.value) || 0.94)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
          </div>

          {/* Colors */}
          <div className="space-y-4">
            <h4 className="text-white font-medium flex items-center">
              <Palette size={16} className="mr-2" />
              Colors
            </h4>

            {/* Color Presets */}
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Color Presets</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((preset) => (
                  <Button
                    key={preset.name}
                    variant="outline"
                    size="sm"
                    onClick={() => handlePresetColorSelect(preset)}
                    className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                  >
                    {preset.name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Current Colors */}
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Current Colors ({colors.length})</Label>
              <div className="flex flex-wrap gap-2">
                {colors.map((color) => (
                  <div
                    key={color}
                    className="flex items-center space-x-1 bg-gray-700 rounded px-2 py-1"
                  >
                    <div
                      className="w-4 h-4 rounded border border-gray-500"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-white text-xs">{color}</span>
                    <button
                      onClick={() => handleRemoveColor(color)}
                      className="text-red-400 hover:text-red-300 text-xs"
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Add Custom Color */}
            <div className="flex space-x-2">
              <Input
                type="color"
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                className="w-12 h-10 bg-gray-700 border-gray-600"
              />
              <Input
                type="text"
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                placeholder="#ff6b6b"
                className="bg-gray-700 border-gray-600 text-white"
              />
              <Button
                onClick={handleAddCustomColor}
                variant="outline"
                size="sm"
                className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
              >
                Add
              </Button>
            </div>
          </div>

          {/* Shapes */}
          <div className="space-y-4">
            <h4 className="text-white font-medium">Shapes</h4>
            <div className="flex space-x-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="square"
                  checked={shapes.includes("square")}
                  onCheckedChange={() => handleShapeToggle("square")}
                  className="text-white border-gray-600"
                />
                <Label htmlFor="square" className="text-gray-300 text-sm cursor-pointer">
                  Squares
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="circle"
                  checked={shapes.includes("circle")}
                  onCheckedChange={() => handleShapeToggle("circle")}
                  className="text-white border-gray-600"
                />
                <Label htmlFor="circle" className="text-gray-300 text-sm cursor-pointer">
                  Circles
                </Label>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t border-gray-700">
            <Button
              type="button"
              onClick={handleTestConfetti}
              variant="outline"
              className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
            >
              <PartyPopper size={16} className="mr-2" />
              Test Confetti
            </Button>

            <div className="flex space-x-2">
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                variant="outline"
                className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {initialAction ? "Update" : "Add"} Confetti
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
